// ============================================================
// account/AccountContext.js — who's signed in and what they paid for: passcodes, plans, and Stripe checkout. Set the SALT (in Passcodes) for each deployment with scripts/make-salt.js.
//
// In this file, in order:
//   Settings for your deployment (plans, Stripe links, legal names)
//   Passcodes
//   Signing in
//   Stripe checkout
//   Buying, renewing and upgrading
// ============================================================

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { readSaved, writeSaved } from "../lib/browser";

/* ============================================================
   Settings for your deployment (plans, Stripe links, legal names)
   ============================================================ */

// ============================================================
// Settings for your deployment — the only part you should need to edit to switch
// payments on. Everything else reads from here.
// ============================================================

// Payments: one Stripe Payment Link per plan (Stripe Dashboard > Payment
// links > New). No server and no keys: the app sends people to the link and
// checks their return. For each link, under "After payment", choose
// "Don't show confirmation page" and redirect to:
//   https://YOUR-DOMAIN/checkout?session_id={CHECKOUT_SESSION_ID}
// Until both plan links are filled in, the app runs in TEST MODE: checkout
// is skipped and workspaces are created without payment (say so on screen).
export const STRIPE = {
  links: {
    department: "https://buy.stripe.com/7sY7sK3Ng0Hpfl6cW86wE00", // $149 a year, one department
    all: "https://buy.stripe.com/7sYcN42Jcbm3c8U09m6wE01", // $499 a year, all departments
    // Optional: a $350 link for moving from one department to all during
    // the year (keeps the same end date). Without it, upgrading uses the
    // "all" link and starts a new year.
    upgrade: "",
  },
  // Optional: Stripe's customer portal login link (Settings > Billing >
  // Customer portal), where people find receipts and invoices.
  customerPortalUrl: "",
};

// The two plans. Both include every feature; they differ only in how many
// departments can use the workspace. Prices must match the Payment Links.
export const PLANS = [
  {
    id: "department",
    name: "One department",
    price: "$149",
    period: "per year",
    blurb: "For one office: the clerk, the sheriff, parks, public works…",
    features: [
      "Every template, fully editable, in your colors and logo",
      "Images, videos and HTML emails for every platform and size",
      "Accessibility checks, one-tap fixes and printable reports",
      "Captions, alt text, post text and other languages",
      "Calendar with holidays, elections and your own dates",
      "One department's content and passcode",
    ],
  },
  {
    id: "all",
    name: "All departments",
    price: "$499",
    period: "per year",
    blurb: "For the whole county, city, court or school district.",
    features: [
      "Everything in One department",
      "Every department's content and templates",
      "Switch departments any time in Settings",
      "A passcode for each department, all from one purchase",
    ],
  },
];
export const planById = (id) => PLANS.find((p) => p.id === id) || PLANS[0];

export const paymentsConfigured = () => Boolean(STRIPE.links.department && STRIPE.links.all);

// Details shown on the About, Privacy and Cookie pages. Fill these in before
// publishing, and have your legal counsel review the policy text
// (components/Site.js). Blank fields fall back to neutral wording.
export const LEGAL = {
  appName: "Gov Social Kit",
  orgName: "Gov Social Kit",        // who operates this site
  contactEmail: "ada@govsocialkit.com", // Contact page, help, passcode support
  mailingAddress: "",               // optional
  phone: "",                        // shown on the Contact page, e.g. "(319) 385-0765"
  hours: "",                        // e.g. "Monday to Friday, 8 AM to 4:30 PM"
  effectiveDate: "September 23, 2026",
  errorReports: false,              // true once MONITORING.endpoint is set (Privacy Policy wording)
};

// Error reporting. Set `endpoint` to a URL that accepts JSON POSTs (your own
// serverless function, a logging service, or an error tracker's intake); see
// hosting/monitoring-endpoint.example.js. With no endpoint, reports stay on
// the device (Settings, "Problems on this device"). Nothing personal is sent:
// see the Error reports section of lib/browser.js for exactly what's included.
export const MONITORING = {
  endpoint: "",
  maxPerSession: 20,
};

/* ============================================================
   Offices (jurisdiction types, departments, states)
   ============================================================ */

// ============================================================
// Jurisdiction types, every office, and what each
// office posts about.
//
// IMPORTANT: short passcodes store each item's POSITION in these lists
// (up to 8 jurisdiction types, 128 offices each). Only ever ADD entries
// at the END of a list; never reorder or remove, or existing passcodes
// would decode to the wrong office. Labels can change freely.
//
// Each office has:
//   archetype  one of the content archetypes below: it decides which
//              content categories the Gallery offers the office
//   topics     what it posts about: calendar events, templates and
//              ready-made content carry topics too, and an office sees
//              what shares a topic with it
//   seesAll    leadership and communications see every topic
// ============================================================

export const JURISDICTION_TYPES = [
  { id: "county", label: "County", category: "county" },
  { id: "city", label: "City", category: "municipal" },
  { id: "town", label: "Town", category: "municipal" },
  { id: "village", label: "Village", category: "municipal" },
  { id: "township", label: "Township", category: "municipal" },
  { id: "court", label: "Court", category: "court" },
  { id: "district", label: "Special district", category: "municipal" },
  { id: "school", label: "School district", category: "school" }, // the 8th and last slot passcodes allow
];

/* ------------------------------------------------------------ offices */

const COUNTY = [
  { id: "general", label: "Board of Commissioners / Supervisors", archetype: "F", seesAll: true },
  { id: "communications", label: "Communications / Public Information", archetype: "A", seesAll: true },
  { id: "administration", label: "County Administration / Manager", archetype: "F", seesAll: true },
  { id: "engineer", label: "County Engineer / Highway", archetype: "E", topics: ["roads"] },
  { id: "emergency", label: "Emergency Management (EMA)", archetype: "A", topics: ["emergency", "weather"] },
  { id: "sheriff", label: "Sheriff", archetype: "A", topics: ["safety", "police"] },
  { id: "auditor", label: "Auditor", archetype: "B", topics: ["finance", "meetings", "elections"] },
  { id: "treasurer", label: "Treasurer", archetype: "B", topics: ["tax", "finance", "motor-vehicle"] },
  { id: "elections", label: "Board of Elections", archetype: "B", topics: ["elections"] },
  { id: "recorder", label: "Recorder", archetype: "B", topics: ["records"] },
  { id: "assessor", label: "Assessor", archetype: "B", topics: ["tax", "property"] },
  { id: "attorney", label: "Prosecutor / County Attorney", archetype: "C", topics: ["courts", "safety"] },
  { id: "planning", label: "Building & Zoning", archetype: "E", topics: ["planning", "meetings", "property"] },
  { id: "health", label: "Public Health", archetype: "A", topics: ["health"] },
  { id: "environmental", label: "Environmental Health", archetype: "A", topics: ["health", "water"] },
  { id: "human-services", label: "Job & Family Services", archetype: "G", topics: ["human-services", "health", "family", "jobs"] },
  { id: "veterans", label: "Veterans Service", archetype: "A", topics: ["veterans"] },
  { id: "conservation", label: "County Parks", archetype: "A", topics: ["parks"] },
  { id: "extension", label: "Extension Office / 4-H", archetype: "A", topics: ["parks", "events", "4h"] },
  { id: "dispatch", label: "911 / Emergency Communications", archetype: "A", topics: ["emergency", "safety", "dispatch"] },
  { id: "medical-examiner", label: "Coroner / Medical Examiner", archetype: "C", topics: ["health"] },
  { id: "hr", label: "Human Resources", archetype: "D", topics: ["jobs"] },
  { id: "it", label: "IT / GIS", archetype: "D", topics: ["it"] },
  { id: "library", label: "County Library", archetype: "A", topics: ["library", "events"] },
  // added later: at the end, so existing passcodes still decode
  { id: "clerk-of-courts", label: "Clerk of Courts", archetype: "C", topics: ["courts", "records", "jury"] },
  { id: "animal-control", label: "Animal Control", archetype: "A", topics: ["animals", "safety"] },
  { id: "soil-water", label: "Soil & Water Conservation", archetype: "G", topics: ["parks", "water", "planning"] },
  { id: "economic", label: "Economic Development", archetype: "G", topics: ["economic", "events", "planning"] },
];

const MUNICIPAL = [
  { id: "mayor", label: "Mayor's Office", archetype: "F", seesAll: true },
  { id: "council", label: "City / Town Council", archetype: "F", seesAll: true },
  { id: "manager", label: "City Manager / Administrator", archetype: "F", seesAll: true },
  { id: "communications", label: "Communications / Public Information", archetype: "A", seesAll: true },
  { id: "clerk", label: "City Clerk", archetype: "F", topics: ["records", "meetings", "elections"] },
  { id: "finance", label: "City Treasurer / Finance", archetype: "B", topics: ["finance", "tax"] },
  { id: "police", label: "Police", archetype: "A", topics: ["safety", "police"] },
  { id: "fire", label: "Fire", archetype: "A", topics: ["safety", "fire", "emergency"] },
  { id: "ems", label: "EMS / Ambulance", archetype: "A", topics: ["safety", "health", "emergency"] },
  { id: "public-works", label: "Public Works", archetype: "E", topics: ["roads", "water", "public-works"] },
  { id: "streets", label: "Streets & Traffic", archetype: "E", topics: ["roads"] },
  { id: "utilities", label: "Water / Sewer Utility", archetype: "E", topics: ["water", "public-works"] },
  { id: "sanitation", label: "Sanitation / Solid Waste", archetype: "E", topics: ["public-works", "recycling"] },
  { id: "parks", label: "Parks & Recreation", archetype: "A", topics: ["parks", "events"] },
  { id: "library", label: "Library", archetype: "A", topics: ["library", "events"] },
  { id: "planning", label: "Planning", archetype: "E", topics: ["planning", "meetings"] },
  { id: "building", label: "Building & Zoning", archetype: "E", topics: ["planning", "property"] },
  { id: "economic", label: "Economic Development / Tourism", archetype: "G", topics: ["economic", "events", "planning"] },
  { id: "housing", label: "Housing Authority", archetype: "G", topics: ["human-services", "property", "housing"] },
  { id: "transit", label: "Transit", archetype: "E", topics: ["roads", "transit"] },
  { id: "airport", label: "Airport Authority", archetype: "A", topics: ["transit"] },
  { id: "emergency", label: "Emergency Management", archetype: "A", topics: ["emergency", "weather"] },
  { id: "health", label: "Health Department", archetype: "A", topics: ["health"] },
  { id: "hr", label: "Human Resources", archetype: "D", topics: ["jobs"] },
  { id: "it", label: "Information Technology", archetype: "D", topics: ["it"] },
  // added later
  { id: "senior-center", label: "Senior Center", archetype: "A", topics: ["seniors", "events", "health"] },
  { id: "law", label: "Law Department / City Attorney", archetype: "D", topics: ["courts"] },
];

const COURT = [
  { id: "court-admin", label: "Court Administration", archetype: "D", seesAll: true, scope: "courts" },
  { id: "clerk-of-court", label: "Clerk of Courts", archetype: "B", topics: ["courts", "records", "jury"] },
  { id: "district-court", label: "Circuit / Common Pleas / District Court", archetype: "C", topics: ["courts"] },
  { id: "magistrate", label: "Municipal / Magistrate Court", archetype: "C", topics: ["courts"] },
  { id: "juvenile", label: "Juvenile Court", archetype: "C", topics: ["courts", "family"] },
  { id: "probate", label: "Probate Court", archetype: "C", topics: ["courts", "records"] },
  { id: "family", label: "Family Court", archetype: "C", topics: ["courts", "family"] },
  { id: "small-claims", label: "Small Claims", archetype: "C", topics: ["courts"] },
  { id: "probation", label: "Probation / Community Corrections", archetype: "C", topics: ["courts", "safety"] },
  { id: "jury", label: "Jury Services", archetype: "C", topics: ["jury", "courts"] },
  { id: "self-help", label: "Self-Help Center", archetype: "C", topics: ["courts", "human-services"] },
  { id: "law-library", label: "Law Library", archetype: "D", topics: ["courts", "library"] },
  { id: "communications", label: "Court Communications", archetype: "C", seesAll: true, scope: "courts" },
  // added later
  { id: "prosecutor", label: "Prosecutor / District Attorney", archetype: "C", topics: ["courts", "safety"] },
  { id: "public-defender", label: "Public Defender", archetype: "C", topics: ["courts"] },
  { id: "register-of-wills", label: "Register of Wills", archetype: "C", topics: ["courts", "records"] },
];

const SCHOOL = [
  { id: "school-board", label: "School Board", archetype: "F", seesAll: true },
  { id: "superintendent", label: "Superintendent's Office", archetype: "F", seesAll: true },
  { id: "communications", label: "Communications", archetype: "A", seesAll: true },
  { id: "business", label: "Business & Finance / Treasurer", archetype: "B", topics: ["school", "finance", "tax"] },
  { id: "schools", label: "Individual Schools", archetype: "A", topics: ["school", "events", "family"] },
  { id: "athletics", label: "Athletics", archetype: "A", topics: ["school", "events", "athletics"] },
  { id: "community-ed", label: "Community Education / Youth Programs", archetype: "A", topics: ["school", "events", "library"] },
  { id: "facilities", label: "Facilities / Operations / Capital Projects", archetype: "E", topics: ["school", "public-works", "weather"] },
  { id: "technology", label: "Technology / IT", archetype: "E", topics: ["school", "it"] },
  { id: "student-services", label: "Special Education / Student Support Services", archetype: "G", topics: ["school", "family", "human-services"] },
  { id: "early-childhood", label: "Early Childhood / Pre-K", archetype: "G", topics: ["school", "family"] },
  { id: "nutrition", label: "Food & Nutrition", archetype: "G", topics: ["school", "health", "family"] },
  { id: "transportation", label: "Transportation", archetype: "G", topics: ["school", "roads", "weather"] },
  { id: "safety", label: "Safety & Security", archetype: "G", topics: ["school", "safety", "emergency"] },
  { id: "hr", label: "Human Resources", archetype: "D", topics: ["jobs"] },
];

const DEPARTMENTS_BY_CATEGORY = { county: COUNTY, municipal: MUNICIPAL, court: COURT, school: SCHOOL };

const typeOf = (typeId) => JURISDICTION_TYPES.find((t) => t.id === typeId || t.label === typeId) || JURISDICTION_TYPES[0];
export const departmentsFor = (typeId) => DEPARTMENTS_BY_CATEGORY[typeOf(typeId).category];

/** The office for a jurisdiction type + office id (falls back to the first). */
export function findDepartment(typeId, departmentId) {
  const list = departmentsFor(typeId);
  return list.find((d) => d.id === departmentId) || list[0];
}

// Fixed order (short passcodes store the position). Add new entries at the end only.
export const STATES = [
  "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut", "Delaware", "Florida", "Georgia",
  "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky", "Louisiana", "Maine", "Maryland",
  "Massachusetts", "Michigan", "Minnesota", "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire",
  "New Jersey", "New Mexico", "New York", "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon", "Pennsylvania",
  "Rhode Island", "South Carolina", "South Dakota", "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington",
  "West Virginia", "Wisconsin", "Wyoming", "District of Columbia",
];

// ---- Department helpers used by the Gallery and Calendar ----

/** The signed-in department's name. */
export const departmentLabel = (session) =>
  session ? findDepartment(session.jurisdiction, session.department).label : "";

/** Leadership and communications roles see everything (court leadership: everything court-related). */
export const seesEverything = (session) => {
  if (!session) return true;
  const d = findDepartment(session.jurisdiction, session.department);
  return Boolean(d.seesAll) && d.scope !== "courts";
};

/** Departments to choose from for this workspace's jurisdiction type. */
export const departmentOptions = (session) => departmentsFor(session ? session.jurisdiction : "county");

/* ============================================================
   Passcodes
   ============================================================ */

// ============================================================
// 12-character passcodes, shown as XXXX-XXXX-XXXX.
//
// 60 bits, written in Crockford base-32 (no I, L, O or U, so it's easy to
// read aloud and type; lowercase, spaces and dashes are all accepted):
//
//   version 2 | jurisdiction type 3 | state 6 | department 7 | plan 1 |
//   expiry month 10 | signature 31
//
// plan: 0 = one department, 1 = all departments. Version 2 is the paid-plans
// format; version 1 codes (the old free/pro plans) are no longer accepted.
//
// Version 3 is a TRIAL passcode (made with tools/trial-passcodes.html): the
// same, but the 10 expiry bits are the exact day it ends (days since January
// 1, 2025, counted round every 1024 days and read as the nearest such day to
// today), so a trial can last 3 days, 7 days, any number up to a year. It
// works until the end of that day, then login says the trial has ended.
//
// The workspace NAME is not stored in the code: you type it with the code,
// and the signature covers it, so a code only works with its own name and
// any change to either is rejected. Everything else (type, state,
// department, plan, expiry) is read back from the code itself.
//
// Like the long format this replaces, it is tamper-evident, not secret-
// proof: the signing salt ships with the app (scrambled). Set it per deployment
// with scripts/make-salt.js.
// ============================================================

const ALPHABET = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";
const VERSION = 2;
const TRIAL_VERSION = 3;
const EPOCH_YEAR = 2025; // expiry is stored as months since January 2025 (up to ~85 years)
// The signing value (SALT), stored scrambled so it isn't readable text in the
// app's code. Set your own with: node scripts/make-salt.js (see PRODUCTION.md).
// <salt> (made by scripts/make-salt.js: don't edit by hand)
const SALT_BYTES = [115,63,164,208,177,239,221,195,156,123,14,104,200,255,144,102,7,50,223,241,204,224,212,191,162,42,29,126,188,206,131,63,119,15,160,227,248,251,243,238,164,47,4];
const SALT_KEY = [48,106,239,165,129,176,181,134,240,26,73,9,229,190,250,82];
// </salt>
// Unscrambled only when a passcode is signed or checked, as bytes (never as text).
const saltBytes = () => new Uint8Array(SALT_BYTES.map((b, i) => b ^ SALT_KEY[i % SALT_KEY.length]));
const HALF = 2 ** 30; // the 60 bits travel as two 30-bit halves (plain numbers, no BigInt)
const SIG_RANGE = 2 ** 31;

const normalizeName = (name) => String(name || "").trim().replace(/\s+/g, " ").toLowerCase();

// 31-bit signature over the details + the (normalized) workspace name.
async function signature(payload, name) {
  const enc = new TextEncoder();
  const key = await crypto.subtle.importKey("raw", enc.encode(`${payload.toString(16)}|${normalizeName(name)}`), "PBKDF2", false, ["deriveBits"]);
  const b = new Uint8Array(await crypto.subtle.deriveBits({ name: "PBKDF2", salt: saltBytes(), iterations: 60000, hash: "SHA-256" }, key, 32));
  return ((b[0] & 0x7f) * 2 ** 24) + (b[1] * 2 ** 16) + (b[2] * 2 ** 8) + b[3];
}

// 6 characters <-> one 30-bit half
function encodeHalf(n) {
  let out = "";
  for (let i = 0; i < 6; i += 1) { out = ALPHABET[n % 32] + out; n = Math.floor(n / 32); }
  return out;
}
function decodeHalf(text) {
  let n = 0;
  for (const ch of text) {
    const v = ALPHABET.indexOf(ch);
    if (v < 0) return null;
    n = n * 32 + v;
  }
  return n;
}

/** Tidy what someone typed: uppercase, drop spaces/dashes, fix look-alikes (I, L -> 1; O -> 0). */
function cleanShortCode(code) {
  return String(code || "").toUpperCase().replace(/[\s-]/g, "").replace(/[IL]/g, "1").replace(/O/g, "0");
}
export const formatShortCode = (code) => cleanShortCode(code).replace(/(.{4})(?=.)/g, "$1-");

// Trials: the day, counted from January 1, 2025 (calendar days where you are).
const DAY_EPOCH = Date.UTC(EPOCH_YEAR, 0, 1);
const dayIndex = (ms) => { const d = new Date(ms); return Math.round((Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) - DAY_EPOCH) / 86400000); };
const endOfDay = (index) => { const d = new Date(DAY_EPOCH + index * 86400000); return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 23, 59, 59, 999).getTime(); };
// The 10 bits hold the day counted round every 1024 days: the nearest such day to today.
const nearestDay = (bits, today = dayIndex(Date.now())) => { let diff = (((bits - today) % 1024) + 1024) % 1024; if (diff >= 512) diff -= 1024; return today + diff; };

const monthIndex = (ms) => { const d = new Date(ms); return (d.getFullYear() - EPOCH_YEAR) * 12 + d.getMonth(); };
const endOfMonth = (index) => new Date(EPOCH_YEAR + Math.floor(index / 12), (index % 12) + 1, 1).getTime() - 1;

/**
 * @param {{type: string, state: string, department: string, plan: "department"|"all", expiresAt: number, trial?: boolean}} data
 *   trial: a trial passcode, good until the end of the day expiresAt falls on
 * @param {string} name  workspace name (not stored in the code, but signed)
 */
export async function generateShortCode({ type, state, department, plan, expiresAt, trial = false }, name) {
  const t = JURISDICTION_TYPES.findIndex((x) => x.id === type || x.label === type);
  const s = STATES.indexOf(state);
  const d = departmentsFor(type).findIndex((x) => x.id === department);
  if (t < 0 || s < 0 || d < 0) throw new Error("Unknown jurisdiction, state or department.");
  const m = trial ? ((dayIndex(expiresAt) % 1024) + 1024) % 1024 : Math.max(0, Math.min(1023, monthIndex(expiresAt)));
  // 29 bits: version | type 3 | state 6 | department 7 | plan 1 | expiry (month, or a trial's day) 10
  const payload = (((((trial ? TRIAL_VERSION : VERSION) * 8 + t) * 64 + s) * 128 + d) * 2 + (plan === "all" ? 1 : 0)) * 1024 + m;
  const sig = await signature(payload, name);
  // 60 bits = payload (29) then signature (31), split into two 30-bit halves.
  const high = payload * 2 + Math.floor(sig / HALF);
  const low = sig % HALF;
  return formatShortCode(encodeHalf(high) + encodeHalf(low));
}

/** Reads a short code back. Throws a friendly error if it's mistyped or doesn't match the name. */
export async function parseShortCode(code, name) {
  const clean = cleanShortCode(code);
  const high = clean.length === 12 ? decodeHalf(clean.slice(0, 6)) : null;
  const low = clean.length === 12 ? decodeHalf(clean.slice(6)) : null;
  if (high === null || low === null) throw new Error("That passcode isn't valid. Check for typos: it's 12 letters and numbers.");
  const payload = Math.floor(high / 2);
  const sig = (high % 2) * HALF + low;
  if (sig >= SIG_RANGE || sig !== (await signature(payload, name))) {
    throw new Error("That passcode doesn't match the workspace name. Check both for typos.");
  }
  let rest = payload;
  const m = rest % 1024; rest = Math.floor(rest / 1024);
  const plan = rest % 2 ? "all" : "department"; rest = Math.floor(rest / 2);
  const d = rest % 128; rest = Math.floor(rest / 128);
  const s = rest % 64; rest = Math.floor(rest / 64);
  const t = rest % 8; const version = Math.floor(rest / 8);
  const type = JURISDICTION_TYPES[t];
  const department = type && departmentsFor(type.id)[d];
  if ((version !== VERSION && version !== TRIAL_VERSION) || !type || !STATES[s] || !department) throw new Error("That passcode isn't valid.");
  const trial = version === TRIAL_VERSION;
  return { type: type.id, typeLabel: type.label, state: STATES[s], department: department.id, plan, trial,
    expiresAt: trial ? endOfDay(nearestDay(m)) : endOfMonth(m) };
}

/* ============================================================
   Signing in
   ============================================================ */

// Sign-in with a passcode. Buying a plan issues a 12-character
// passcode (Passcodes, above); signing in checks it against the workspace name.
// No server and no password reset: the passcode IS the credential.
//
// Every passcode carries its plan ("department" or "all") and the month it
// ends. Buying, renewing and upgrading all happen on Stripe; when the
// browser comes back from checkout, applyPurchase() issues the new passcode
// (see account/AccountScreens.js, Back from Stripe, for the whole flow).
//
// Honest limit: it all runs in the browser, so the signing salt ships with
// the app. Passcodes are tamper-evident (you can't edit one and have it still
// work) but not tamper-proof. Move generateShortCode to a small server if
// you need real protection.

const AuthContext = createContext(null);
const SESSION_KEY = "gct_session";
const YEAR_MS = 365 * 86400000;
const DAY_MS = 86400000;

/** Days left on a passcode (negative once it has ended). */
export const daysLeft = (expiresAt, now = Date.now()) => Math.ceil((expiresAt - now) / DAY_MS);
export const formatDate = (ms) => new Date(ms).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

/** A year from the later of today and the current end date (renewing early loses nothing). */
export const nextYear = (expiresAt = 0, now = Date.now()) => Math.max(now, expiresAt) + YEAR_MS;

/** Checks a passcode with the workspace name and returns the session (what login does). */
export async function verify(code, name) {
  const d = await parseShortCode(code, name);
  if (d.expiresAt < Date.now()) {
    const err = new Error(d.trial
      ? `This trial ended on ${formatDate(d.expiresAt)}. Choose a plan to keep using your workspace: your designs are still here.`
      : `This passcode ended on ${formatDate(d.expiresAt)}. Renew it to keep using your workspace: your designs are still here.`);
    err.expired = { code: formatShortCode(code), name: String(name).trim(), plan: d.plan, trial: d.trial };
    throw err;
  }
  return {
    code: formatShortCode(code), name: String(name).trim(),
    jurisdiction: d.type, jurisdictionType: d.typeLabel, state: d.state,
    department: d.department, plan: d.plan, expiresAt: d.expiresAt, trial: d.trial,
  };
}

// Where the session is remembered: localStorage ("keep me signed in"), or
// sessionStorage on a shared computer (gone when the browser closes).
const remembered = () => readSaved(SESSION_KEY) || readSaved(SESSION_KEY, { session: true });
const forget = () => { writeSaved(SESSION_KEY, null); writeSaved(SESSION_KEY, null, { session: true }); };

// Slows down guessing: after 5 wrong passcodes, wait 30 seconds (per tab).
const failures = { count: 0, until: 0 };
function checkThrottle() {
  if (Date.now() < failures.until) {
    throw new Error(`Too many tries. Wait ${Math.ceil((failures.until - Date.now()) / 1000)} seconds and try again.`);
  }
}
function noteFailure() {
  failures.count += 1;
  if (failures.count >= 5) { failures.until = Date.now() + 30000; failures.count = 0; }
}

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const saved = remembered();
        if (saved) setSession({ ...(await verify(saved.code, saved.name)), keep: saved.keep !== false });
      } catch {
        forget(); // ended, tampered with, or unreadable: sign out cleanly
      }
      setReady(true);
    })();
  }, []);

  // A passcode that ends while the app is open (a trial, say) signs out then.
  useEffect(() => {
    if (!session || !session.expiresAt) return undefined;
    const wait = session.expiresAt - Date.now();
    if (wait > 2 ** 31 - 1) return undefined; // more than ~24 days away: checked again next time the app opens
    const timer = setTimeout(() => { forget(); setSession(null); }, Math.max(0, wait));
    return () => clearTimeout(timer);
  }, [session]);

  const persist = useCallback((next, keep = true) => {
    setSession({ ...next, keep });
    forget();
    // In private browsing this can't be saved: the session then lasts this tab.
    writeSaved(SESSION_KEY, { code: next.code, name: next.name, keep }, { session: !keep });
  }, []);

  const login = useCallback(async ({ name, code, keep = true }) => {
    checkThrottle();
    try {
      persist(await verify(code, name), keep);
      failures.count = 0;
    } catch (err) {
      if (!err.expired) noteFailure();
      throw err;
    }
  }, [persist]);

  /**
   * Issues the passcode for something just paid for (or, in test mode, just
   * chosen), signs in with it and returns it.
   *   { kind: "new", plan, name, jurisdiction, state, department }
   *   { kind: "renew", name, code }         another year, same details
   *   { kind: "upgrade", name, code, keepEnd }  one department -> all
   */
  const applyPurchase = useCallback(async (p) => {
    let data;
    if (p.kind === "new") {
      data = { type: p.jurisdiction, state: p.state, department: p.department, plan: p.plan, expiresAt: nextYear() };
    } else {
      const d = await parseShortCode(p.code, p.name); // an ended passcode is fine here: that's what renewing is for
      data = {
        type: d.type, state: d.state, department: d.department,
        plan: p.kind === "upgrade" ? "all" : d.plan,
        expiresAt: p.kind === "upgrade" && p.keepEnd ? Math.max(d.expiresAt, Date.now() + DAY_MS) : nextYear(d.expiresAt),
      };
    }
    const code = await generateShortCode(data, p.name);
    persist(await verify(code, p.name), session ? session.keep : true);
    return code;
  }, [persist, session]);

  // All-departments plans can switch department: a new passcode for it, same
  // plan and end date. (The old passcode keeps signing in to the old one.)
  const changeDepartment = useCallback(async (department) => {
    if (!session) throw new Error("Sign in first.");
    if (session.plan !== "all") throw new Error("Your plan covers one department. Upgrade to all departments to switch.");
    const data = { type: session.jurisdiction, state: session.state, department, plan: session.plan, expiresAt: session.expiresAt };
    const code = await generateShortCode(data, session.name);
    persist(await verify(code, session.name), session.keep);
    return code;
  }, [session, persist]);

  const logout = useCallback(() => {
    setSession(null);
    forget();
  }, []);

  const value = useMemo(
    () => ({ session, ready, login, logout, applyPurchase, changeDepartment }),
    [session, ready, login, logout, applyPurchase, changeDepartment]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/* ============================================================
   Stripe checkout
   ============================================================ */

// ============================================================
// Stripe checkout with no server and no keys.
//
// Stripe's supported way to take payments with no backend is a Payment
// Link (stripe.redirectToCheckout was removed in 2025). The flow:
//
//   1. startCheckout(link, { pending, email, reference })
//        saves `pending` (what was bought, for whom) in this browser,
//        marks this tab as "went to checkout", and goes to the link.
//   2. Stripe takes the payment and sends the browser back to
//        /checkout?session_id=cs_...   (set on the Payment Link)
//   3. readCheckoutReturn()
//        checks the return looks real, reads `pending` back, and makes
//        sure the same checkout can't be used twice in this browser.
//
// How "looks real" is judged: a ?session_id=cs_... in the address proves
// nothing alone (anyone can type it). So it must ALSO come with the marker
// this tab set before leaving, and (when the browser sends one) a referrer
// from Stripe. That stops bookmarked or shared success links from working
// for someone who never paid. It is not proof of payment: only a server
// (a Stripe webhook) can confirm that. The Stripe checkout ID is shown on
// the receipt screen and sent as client_reference_id, so every passcode can
// be matched to a payment in the Stripe Dashboard.
// ============================================================

const MARKER_KEY = "sc_checkout_started";   // sessionStorage: this tab went to checkout
const PENDING_KEY = "sc_pending_checkout";  // localStorage: what was being bought
const USED_KEY = "sc_used_checkouts";       // localStorage: checkout IDs already turned into passcodes
const MAX_AGE_MS = 2 * 60 * 60 * 1000;      // two hours to pay and come back
const STRIPE_ORIGINS = ["https://checkout.stripe.com", "https://buy.stripe.com"];

/** Stripe allows letters, numbers, - and _ (up to 200) in client_reference_id. */
export const referenceId = (...parts) => parts.join("_").replace(/[^A-Za-z0-9_-]+/g, "-").slice(0, 200);

/**
 * Goes to a Stripe Payment Link.
 * @param {string} link  https://buy.stripe.com/...
 * @param {{ pending: object, email?: string, reference?: string }} opts
 */
export function startCheckout(link, { pending, email, reference }) {
  const url = new URL(link);
  if (reference) url.searchParams.set("client_reference_id", reference);
  if (email) url.searchParams.set("prefilled_email", email);
  writeSaved(PENDING_KEY, { ...pending, ts: Date.now() });
  writeSaved(MARKER_KEY, Date.now(), { session: true });
  window.location.assign(url.toString());
}

/** Removes ?session_id=... from the address, so it isn't bookmarked or shared. */
function stripSessionId() {
  const params = new URLSearchParams(window.location.search);
  params.delete("session_id");
  window.history.replaceState({}, "", window.location.pathname + (params.toString() ? `?${params}` : "") + window.location.hash);
}

/**
 * Reads the return from Stripe, once.
 * @returns {{ ok: true, sessionId, pending } | { ok: false, reason, sessionId? }}
 */
export function readCheckoutReturn() {
  const sessionId = new URLSearchParams(window.location.search).get("session_id");
  stripSessionId();
  if (!sessionId || !/^cs_(test_|live_)?[A-Za-z0-9]{10,}$/.test(sessionId)) {
    return { ok: false, reason: "There's no Stripe checkout to finish here." };
  }
  const used = readSaved(USED_KEY) || [];
  if (used.includes(sessionId)) {
    return { ok: false, sessionId, used: true, reason: "This checkout has already been used to make a passcode." };
  }
  const started = Number(readSaved(MARKER_KEY, { session: true }));
  const pending = readSaved(PENDING_KEY);
  const fresh = (t) => Number.isFinite(t) && Date.now() - t < MAX_AGE_MS;
  let fromStripe = true;
  try { fromStripe = !document.referrer || STRIPE_ORIGINS.includes(new URL(document.referrer).origin); } catch { fromStripe = false; }
  if (!fresh(started) || !pending || !fresh(pending.ts) || !fromStripe) {
    return { ok: false, sessionId, reason: "We couldn't confirm this return from Stripe in this browser tab." };
  }
  return { ok: true, sessionId, pending };
}

/** Call once the passcode is made: the same checkout can't make another. */
export function finishCheckout(sessionId) {
  const used = readSaved(USED_KEY) || [];
  writeSaved(USED_KEY, [sessionId, ...used].slice(0, 50));
  writeSaved(PENDING_KEY, null);
  writeSaved(MARKER_KEY, null, { session: true });
}

/* ============================================================
   Buying, renewing and upgrading
   ============================================================ */

// What each purchase costs and where it goes. One place for the whole flow:
//
//   new workspace:  pricing -> the form (plan, name, office) -> Stripe
//                   -> /checkout -> passcode issued -> save it -> workspace
//   renew:          log in with an ended passcode, or Settings -> Stripe
//                   -> /checkout -> a new passcode, a year after the old end date
//   upgrade:        Settings (one department -> all) -> Stripe
//                   -> /checkout -> a new passcode for all departments
//
// In test mode (no Payment Links in the settings at the top of this file) the Stripe step is skipped.

/** The Payment Link for a purchase. */
export function linkFor(pending) {
  if (pending.kind === "upgrade") return STRIPE.links.upgrade || STRIPE.links.all;
  return STRIPE.links[pending.plan];
}

/** The price shown on the button for a purchase. */
export function priceFor(pending) {
  if (pending.kind === "upgrade") return STRIPE.links.upgrade ? "$350" : planById("all").price;
  return planById(pending.plan).price;
}

/**
 * Buys, renews or upgrades. With payments set up, this leaves for Stripe
 * (and returns nothing); in test mode it issues the passcode straight away.
 * @param pending  { kind: "new"|"renew"|"upgrade", plan, name, ... } (see auth.js applyPurchase)
 */
export async function purchase(pending, { applyPurchase, email }) {
  const full = pending.kind === "upgrade" ? { ...pending, keepEnd: Boolean(STRIPE.links.upgrade) } : pending;
  if (!paymentsConfigured()) {
    return { code: await applyPurchase(full), details: { ...full, test: true } };
  }
  startCheckout(linkFor(full), {
    pending: full,
    email: email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? email.trim() : undefined,
    // Shows in the Stripe Dashboard next to the payment, to match it to a workspace.
    reference: referenceId("gsk", full.kind, full.kind === "upgrade" ? "all" : full.plan, full.name),
  });
  return null;
}
